import { useState } from 'react';
import { revisionIndex, revisionsInOrder, type Id, type Project, localeTag } from '@elec/domain';
import type { Translate } from '@elec/i18n';
import { Dialog } from './ui.tsx';

/** Revision list: create the next index, compare a revision with now, or go back to it. */
export function RevisionsDialog(props: {
  project: Project;
  t: Translate;
  locale: 'fr' | 'en' | 'nl';
  onClose(): void;
  onCreate(description: string): void;
  onCompare(id: Id): void;
  onRestore(id: Id): void;
}) {
  const { t, project } = props;
  const revs = revisionsInOrder(project);
  const next = revisionIndex(revs.length);
  const [text, setText] = useState('');
  const date = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString(localeTag(props.locale));
  return (
    <Dialog
      title={t('rev.title')}
      wide
      onClose={props.onClose}
      actions={<button onClick={props.onClose}>{t('dialog.close')}</button>}
    >
      {revs.length === 0 ? (
        <p className="muted">{t('rev.none')}</p>
      ) : (
        <div className="table-scroll">
          <table data-testid="rev-table">
            <thead>
              <tr>
                <th>{t('rev.index')}</th>
                <th>{t('rev.date')}</th>
                <th>{t('rev.author')}</th>
                <th>{t('rev.description')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {revs.map((r) => (
                <tr key={r.id}>
                  <td className="code">{r.index}</td>
                  <td className="nowrap">{date(r.date)}</td>
                  <td>{r.author}</td>
                  <td>{r.description}</td>
                  <td className="nowrap">
                    <button
                      className="link"
                      onClick={() => props.onCompare(r.id)}
                      data-testid={`rev-compare-${r.index}`}
                    >
                      {t('rev.compare')}
                    </button>{' '}
                    <button
                      className="link"
                      onClick={() => props.onRestore(r.id)}
                      data-testid={`rev-restore-${r.index}`}
                    >
                      {t('rev.restore')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3 className="dialog-sub">{t('rev.new')}</h3>
      <p className="small">{t('rev.newHint', { index: next })}</p>
      <div className="chat-input">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('rev.description')}
          style={{ flex: 1 }}
          data-testid="rev-text"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && text.trim()) props.onCreate(text.trim());
          }}
        />
        <button
          className="primary"
          disabled={!text.trim()}
          onClick={() => props.onCreate(text.trim())}
          data-testid="rev-create"
        >
          {t('rev.create', { index: next })}
        </button>
      </div>
    </Dialog>
  );
}

/** Ask for a short name. */
export function NameDialog(props: {
  title: string;
  label: string;
  initial?: string;
  ok: string;
  cancel: string;
  onClose(): void;
  onOk(name: string): void;
}) {
  const [v, setV] = useState(props.initial ?? '');
  return (
    <Dialog
      title={props.title}
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{props.cancel}</button>
          <button
            className="primary"
            disabled={!v.trim()}
            onClick={() => props.onOk(v.trim())}
            data-testid="name-ok"
          >
            {props.ok}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="name-input">{props.label}</label>
        <input
          id="name-input"
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && v.trim() && props.onOk(v.trim())}
          data-testid="name-input"
        />
      </div>
    </Dialog>
  );
}
